import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

// material-ui
import { useTheme, alpha } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import nopermission from 'assets/images/permission/no-permission.png';

// assets - ant design icons
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';

// project icons
import {
  FilterIcon,
  ModernIconMore,
  reloadIcon as ReloadIcon,
  SortFilterIcon,
  TemplateIcon,
  InprogressChipIcon,
  PendingChipIcon,
  CompleteChipIcon,
  ActiveTaskIcon,
  ModernBoardIcon,
  ModernListIcon
} from 'utils/TaskManagementIcon/taskmanagemeneticon';

// local components & data
import TaskCard from './TaskCard';
import TaskForm from './TaskForm';
import { TaskItem, TaskStage, TaskManagementDrawerProps, TaskFilters, TaskPriorityEnum, TaskStatusEnum, getStageId } from 'types/taskManagement/TaskManagement';
import { useGetTaskList, useDeleteTask } from 'api/TaskManagement/taskManagement';
import { useGetUserLookupList } from 'api/user';
import { FunnelPlotOutlined } from '@ant-design/icons';
import { Tooltip } from '@mui/material';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { hasAddPermission, hasDeletePermission, hasEditPermission, hasReadPermission } from 'utils/permissions';
import { useGetPermissions } from 'api/authUser';
import { GetFormattedDateOnly, GetFormattedTimeOnly, GetFriendlyTaskDueDate } from 'utils/commonFunctions';
import { formatTatDuration } from 'utils/tatUtils';
import { mutate } from 'swr';
import { useApplyTaskTemplate, useGetTaskTemplateLookupList } from 'api/taskTemplates';
import { isTaskDue, isTaskOverdue, isTaskUpcoming, isTaskInProgress, isTaskCompleted, calculateDashboardCounts, MiniDashboardTileKey } from 'components/TaskManagement/miniDashboardUtils';
import SelectTaskTemplateModal from '../taskTemplates/SelectTaskTemplateModal';
import { showSuccess, showWarning } from 'utils/errorHandler';
import TaskTemplateModal from 'sections/taskTemplates/TaskTemplateModal';
import { useGetPPFProjectByID, useUpdateProjectStageSummary } from 'api/PPFUser/ppfProject';
import useAuth from 'hooks/useAuth';

const getDueOnDateRange = (filter: string, customRange: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => {
  const now = dayjs();
  switch (filter) {
    case 'Today':
      return {
        from: now.startOf('day').toISOString(),
        to: now.endOf('day').toISOString()
      };
    case 'Tomorrow':
      const tomorrow = now.add(1, 'day');
      return {
        from: tomorrow.startOf('day').toISOString(),
        to: tomorrow.endOf('day').toISOString()
      };
    case 'This Week':
      return {
        from: now.startOf('week').toISOString(),
        to: now.endOf('week').toISOString()
      };
    case 'Next Week':
      const nextWeek = now.add(1, 'week');
      return {
        from: nextWeek.startOf('week').toISOString(),
        to: nextWeek.endOf('week').toISOString()
      };
    case 'Later':
      return {
        from: now.add(1, 'week').endOf('week').toISOString(),
        to: null
      };
    case 'Custom':
      return {
        from: customRange[0] ? customRange[0].startOf('day').toISOString() : null,
        to: customRange[1] ? customRange[1].endOf('day').toISOString() : null
      };
    default:
      return { from: null, to: null };
  }
};

const getCreatedOnDateRange = (filter: string, customRange: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => {
  const now = dayjs();
  switch (filter) {
    case 'Today':
      return {
        from: now.startOf('day').toISOString(),
        to: now.endOf('day').toISOString()
      };
    case 'Yesterday':
      const yesterday = now.subtract(1, 'day');
      return {
        from: yesterday.startOf('day').toISOString(),
        to: yesterday.endOf('day').toISOString()
      };
    case 'This Week':
      return {
        from: now.startOf('week').toISOString(),
        to: now.endOf('week').toISOString()
      };
    case 'Last Week':
      const lastWeek = now.subtract(1, 'week');
      return {
        from: lastWeek.startOf('week').toISOString(),
        to: lastWeek.endOf('week').toISOString()
      };
    case 'Before':
      return {
        from: null,
        to: now.subtract(1, 'week').startOf('week').toISOString()
      };
    case 'Custom':
      return {
        from: customRange[0] ? customRange[0].startOf('day').toISOString() : null,
        to: customRange[1] ? customRange[1].endOf('day').toISOString() : null
      };
    default:
      return { from: null, to: null };
  }
};

export default function TaskManagementDrawer({ open, onClose, stage, openTaskModal, selectedProjectID }: TaskManagementDrawerProps) {


  const featureID = 47;
  const { permissions } = useGetPermissions();
  const { user } = useAuth();
  const allowView = hasReadPermission(featureID, permissions);
  const allowAdd = hasAddPermission(featureID, permissions);
  const allowEdit = hasEditPermission(featureID, permissions);
  const allowDelete = hasDeletePermission(featureID, permissions);
  const allowAddTaskTemplate = hasAddPermission(48, permissions);

  const theme = useTheme();
  const navigate = useNavigate();
  const { projectID } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isPlanReview = location.pathname.toLowerCase().includes('planreviews');
  const projectIdNumber = projectID || selectedProjectID ? Number(projectID || selectedProjectID) : null;
  const taskModule = projectIdNumber ? (isPlanReview ? 2 : 1) : 0;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedStage, setSelectedStage] = useState<string>(stage ?? TaskStage.ALL);
  const [selectedSort, setSelectedSort] = useState<string>('Most Relevant');
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const isSortMenuOpen = Boolean(sortAnchorEl);

  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const isFilterOpen = Boolean(filterAnchorEl);

  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [filterDueOn, setFilterDueOn] = useState<string>('All');
  const [filterAssignedTo, setFilterAssignedTo] = useState<string | number>('All');
  const [filterPriority, setFilterPriority] = useState<string>('All');
  const [filterCreatedOn, setFilterCreatedOn] = useState<string>('All');

  const [customDueRange, setCustomDueRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [customCreatedRange, setCustomCreatedRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  const sanitizeDateRange = (range: any): [dayjs.Dayjs | null, dayjs.Dayjs | null] => {
    if (!Array.isArray(range)) return [null, null];
    const start = range[0] ? (dayjs.isDayjs(range[0]) ? range[0] : dayjs(range[0])) : null;
    const end = range[1] ? (dayjs.isDayjs(range[1]) ? range[1] : dayjs(range[1])) : null;
    return [start && start.isValid() ? start : null, end && end.isValid() ? end : null];
  };

  // Quick Dashboard Chip Filter state (independent of popover filters)
  const [selectedDashboardChip, setSelectedDashboardChip] = useState<MiniDashboardTileKey | null>(null);

  // Applied states that actually trigger SWR
  const [appliedStatus, setAppliedStatus] = useState<string>('All');
  const [appliedDueOn, setAppliedDueOn] = useState<string>('All');
  const [appliedAssignedTo, setAppliedAssignedTo] = useState<string | number>('All');
  const [appliedPriority, setAppliedPriority] = useState<string>('All');
  const [appliedCreatedOn, setAppliedCreatedOn] = useState<string>('All');

  const [appliedCustomDueRange, setAppliedCustomDueRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [appliedCustomCreatedRange, setAppliedCustomCreatedRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  // Calculate active filters count for badge display
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedStatus !== 'All') count++;
    if (appliedDueOn !== 'All') count++;
    if (appliedAssignedTo !== 'All') count++;
    if (appliedPriority !== 'All') count++;
    if (appliedCreatedOn !== 'All') count++;
    return count;
  }, [appliedStatus, appliedDueOn, appliedAssignedTo, appliedPriority, appliedCreatedOn]);

  const { data: userLookupData } = useGetUserLookupList();
  const { data: taskTemplates } = useGetTaskTemplateLookupList(taskModule)

  const dueDates = useMemo(() => getDueOnDateRange(appliedDueOn, appliedCustomDueRange), [appliedDueOn, appliedCustomDueRange]);
  const createdDates = useMemo(() => getCreatedOnDateRange(appliedCreatedOn, appliedCustomCreatedRange), [appliedCreatedOn, appliedCustomCreatedRange]);

  const mappedPriorityValue = useMemo(() => {
    switch (appliedPriority) {
      case 'Urgent':
        return TaskPriorityEnum.Urgent;
      case 'High':
        return TaskPriorityEnum.High;
      case 'Normal':
        return TaskPriorityEnum.Normal;
      case 'Low':
        return TaskPriorityEnum.Low;
      default:
        return null;
    }
  }, [appliedPriority]);

  const apiFilters = useMemo<TaskFilters>(() => ({
    taskModule,
    projectID: projectIdNumber,
    status: null,
    priority: mappedPriorityValue,
    assignedUserIDs: appliedAssignedTo === 'All' ? [] : [Number(appliedAssignedTo)],
    dueDateFrom: dueDates.from,
    dueDateTo: dueDates.to,
    createdDateFrom: createdDates.from,
    createdDateTo: createdDates.to
  }), [taskModule, projectIdNumber, mappedPriorityValue, appliedAssignedTo, dueDates, createdDates]);

  const { data: apiTasks, isLoading: loading, error, mutate: refetchTasks } = useGetTaskList(apiFilters, open && allowView);

  useEffect(() => {
    if (loading) return;

    const openTaskForm =
      searchParams.get('openTaskForm') === 'true' ||
      localStorage.getItem('openTaskForm') === 'true';

    const taskID =
      searchParams.get('taskID') || localStorage.getItem('taskID');


    if (openTaskForm && taskID) {
      const task = apiTasks?.find((x: any) => x.taskID === Number(taskID));


      if (!task) {
        showWarning('Task not found. It may have been deleted or is no longer available')
        return
      };

      setSelectedTaskForEdit(task);
      setOpenAddTaskModal(true);
    }

    const params = new URLSearchParams(searchParams);

    [
      'openViewTaskPanel',
      'openTaskForm',
      'taskID',
      'taskModule',
      'uniqueCode'
    ].forEach((key) => {
      params.delete(key);
      localStorage.removeItem(key);
    });

    setSearchParams(params, { replace: true });
  }, [loading, apiTasks]);

  const handleRefresh = () => {
    refetchTasks();
  };

  const handleDeleteTask = async (task: TaskItem) => {
    try {
      await useDeleteTask(task.taskID);
      refetchTasks();

      if (task.projectID && location.pathname.includes("ppf/projects")) {
        useGetPPFProjectByID(task.projectID)
      }

      if (location.pathname === '/ppf/taskList') {
        mutate((key: any) => typeof key === 'string' ? key.includes('Task/GetList') : Array.isArray(key) && key[0]?.includes('Task/GetList'));
      }

      mutate((key: any) => {
        if (!key) return false;
        const keyStr = typeof key === 'string' ? key : (Array.isArray(key) ? key[0] : JSON.stringify(key));
        return keyStr.includes('/TaskBoard') || keyStr.includes('Task/GetPanelList');
      });

      if (task.projectID && location.pathname.includes('ppf/projects/workspace')) {
        useUpdateProjectStageSummary([task.stageID], task.projectID)
      }
      if (task.projectID && location.pathname.includes('ppf/planReviews')) {
        await mutate(`Project/PlanReview/GetTaskSummary/${task.projectID}`);
      }
    } catch (error) {
      // Errors are already handled inside useDeleteTask (via handleApiError)
    }
  };

  const handleOpenFilterPopover = (event: React.MouseEvent<HTMLElement>) => {
    // Sync draft states with applied states on opening
    setFilterStatus(appliedStatus);
    setFilterDueOn(appliedDueOn);
    setFilterAssignedTo(appliedAssignedTo);
    setFilterPriority(appliedPriority);
    setFilterCreatedOn(appliedCreatedOn);
    setCustomDueRange(appliedCustomDueRange);
    setCustomCreatedRange(appliedCustomCreatedRange);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = () => {
    setAppliedStatus(filterStatus);
    setAppliedDueOn(filterDueOn);
    setAppliedAssignedTo(filterAssignedTo);
    setAppliedPriority(filterPriority);
    setAppliedCreatedOn(filterCreatedOn);
    setAppliedCustomDueRange(customDueRange);
    setAppliedCustomCreatedRange(customCreatedRange);
    handleCloseFilterPopover();
  };

  const handleClearFilters = () => {
    setFilterStatus('All');
    setFilterDueOn('All');
    setFilterAssignedTo('All');
    setFilterPriority('All');
    setFilterCreatedOn('All');
    setCustomDueRange([null, null]);
    setCustomCreatedRange([null, null]);
    setAppliedStatus('All');
    setAppliedDueOn('All');
    setAppliedAssignedTo('All');
    setAppliedPriority('All');
    setAppliedCreatedOn('All');
    setAppliedCustomDueRange([null, null]);
    setAppliedCustomCreatedRange([null, null]);
    setSelectedDashboardChip(null);
  };

  const handleOpenSortMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleCloseSortMenu = () => {
    setSortAnchorEl(null);
  };

  const handleSelectSort = (sortOption: string) => {
    setSelectedSort(sortOption);
    handleCloseSortMenu();
  };

  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const isMoreMenuOpen = Boolean(moreMenuAnchor);

  // Add Task Modal State
  const [openAddTaskModal, setOpenAddTaskModal] = useState<boolean>(false);
  const [selectedTaskForEdit, setSelectedTaskForEdit] = useState<TaskItem | null>(null);
  const [openTaskTemplateSelectID, setOpenTaskTemplateSelectID] = useState<number | null>(null)
  const [isApplying, setIsApplying] = useState(false);
  const [openTaskTemplateModal, setOpenTaskTemplateModal] = useState(false);

  useEffect(() => {
    if (openTaskModal && allowAdd) {
      setSelectedTaskForEdit(null);
      setOpenAddTaskModal(true);
    }
  }, [openTaskModal, allowAdd]);

  const handleOpenAddTaskModal = (task?: TaskItem | null) => {
    setSelectedTaskForEdit(task || null);
    setOpenAddTaskModal(true);
  };

  const handleOpenMoreMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleCloseMoreMenu = () => {
    setMoreMenuAnchor(null);
    setOpenTaskTemplateSelectID(null)
  };

  const handleConfirmTaskTemplate = async () => {
    try {
      setIsApplying(true)
      await useApplyTaskTemplate({ projectID: Number(projectID), taskTemplateID: openTaskTemplateSelectID as number })
      refetchTasks()
      showSuccess("Template task imported successfully!")
    } finally {
      handleCloseMoreMenu()
      setIsApplying(false)
    }
  }

  // Map backend response to local TaskItem structure
  const mappedTasks = useMemo(() => {
    const rawList = apiTasks || [];
    return rawList.map((item: any) => {
      const isOverdue = item.dueOn ? dayjs(typeof item.dueOn === 'string' ? item.dueOn.replace(/Z$/i, '').replace(/[+-]\d{2}:\d{2}$/, '') : item.dueOn).isBefore(dayjs(), 'day') : false;
      const assignedUser = item.assignedUsers && item.assignedUsers.length > 0 ? {
        name: item.assignedUsers[0].userFullName || '',
        extraCount: item.assignedUsers.length - 1,
        extraUsers: item.assignedUsers.slice(1).map((u: any) => u.userFullName || '')
      } : undefined;

      const notes = item.latestNote ? [
        {
          id: String(item.latestNote.taskNoteID || item.latestNote.id || 'latest'),
          taskNoteID: item.latestNote.taskNoteID,
          authorName: item.latestNote.createdByUserName || item.latestNote.createdByName || item.latestNote.authorName || 'User',
          authorInitials: (item.latestNote.createdByUserName || item.latestNote.createdByName || item.latestNote.authorName || 'User')
            .split(' ')
            .map((n: string) => n[0] || '')
            .join('')
            .toUpperCase()
            .slice(0, 2),
          createdDate: item.latestNote.createdOn
            ? `${GetFormattedDateOnly(item.latestNote.createdOn)} on ${GetFormattedTimeOnly(item.latestNote.createdOn)}`
            : item.latestNote.createdDate || '',
          rawCreatedOn: item.latestNote.createdOn,
          noteText: item.latestNote.noteFormatted || item.latestNote.notePlain || item.latestNote.noteText || '',
          notePlain: item.latestNote.notePlain || item.latestNote.noteText || '',
          noteFormatted: item.latestNote.noteFormatted || '',
          attachments: item.latestNote.attachments || [],
          canEdit: item.latestNote.canEdit ?? true,
          canDelete: item.latestNote.canDelete ?? true
        }
      ] : (Array.isArray(item.notes) ? item.notes : []);

      const createdBy = item.createdByUserName ? {
        authorName: item.createdByUserName,
        createdDate: item.createdOn || ''
      } : undefined;

      const updatedBy = (item.updatedByUserName || item.updatedOn) ? {
        authorName: item.updatedByUserName || '',
        updatedDate: item.updatedOn || ''
      } : undefined;

      return {
        taskID: String(item.taskID || item.primaryKeyID || ''),
        title: item.taskTitle || '',
        status: item.status || item.taskStatusName || (item.taskStatus === 0 ? 'Pending' : item.taskStatus === 1 ? 'In-Progress' : item.taskStatus === 2 ? 'Completed' : item.taskStatus === 3 ? 'Cancelled' : 'Pending'),
        taskStatus: item.taskStatus !== undefined ? item.taskStatus : item.statusEnum,
        taskStatusName: item.taskStatusName || item.status || '',
        statusEnum: item.taskStatus !== undefined ? item.taskStatus : item.statusEnum,
        stage: item.stageName || undefined,
        stageID: item.stageID || null,
        dueOn: item.dueOn || item.dueDate || '',
        dueDate: item.dueOn || item.dueDate || '',
        isOverdue,
        priority: item.priority || item.taskPriorityName || 'Normal',
        assignedUser,
        assignedUserIDs: item.assignedUsers?.map((u: any) => u.userID) || [],
        projectID: item.projectID || null,
        description: item.descrFormatted || item.description || item.descrPlain || '',
        attachments: item.attachments || [],
        totalAttachmentCount: item.totalAttachmentCount ?? (item.attachments?.length || 0),
        notes,
        createdBy,
        updatedBy,
        completedByUserName: item.completedByUserName || null,
        completedOn: item.completedOn || null,
        startedOn: item.startedOn || null,
        elapsedMinutes: item.elapsedMinutes ?? null,
        updatedOn: item.updatedOn || null,
        visibleToContractor: item.visibleToContractor ?? false,
        canEdit: item.canEdit ?? false,
        canDelete: item.canDelete ?? false,
        canChangeStatus: item.canChangeStatus ?? false
      };
    });
  }, [apiTasks]);

  // Status Counts calculation for top header chips
  const statusCounts = useMemo(() => {
    const currentStageID = selectedStage !== TaskStage.ALL ? getStageId(selectedStage) : null;

    const stageFilteredTasks = (mappedTasks || []).filter((t: any) => {
      // 1. Project match (type-safe comparison)
      if (projectIdNumber !== null && t.projectID && Number(t.projectID) !== projectIdNumber) {
        return false;
      }

      // 2. Stage match (checks both stage string and stageID)
      if (!isPlanReview && selectedStage !== TaskStage.ALL) {
        const isStageMatch = t.stage === selectedStage || (currentStageID !== null && t.stageID === currentStageID);
        if (!isStageMatch) return false;
      }

      return true;
    });

    const counts = calculateDashboardCounts(stageFilteredTasks);

    return {
      due: counts.dueCount ?? 0,
      overdue: counts.overdueCount ?? 0,
      inProgress: counts.inProgressCount ?? 0,
      completed: counts.completedCount ?? 0,
      upcoming: counts.upcomingCount ?? 0
    };
  }, [mappedTasks, projectIdNumber, selectedStage, isPlanReview]);

  // Deep search across all task fields & filter tasks locally
  const filteredTasks = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    return mappedTasks.filter((task: any) => {
      // 1. Deep Search Query across all fields
      if (q) {
        const inTitle = task.title?.toLowerCase().includes(q);
        const inDesc = task.description?.toLowerCase().includes(q);
        const inStatus = task.status?.toLowerCase().includes(q);
        const inStage = task.stage?.toLowerCase().includes(q);
        let searchDateTerm = q;
        if (['tommorow', 'tomorow'].some((term) => q.includes(term))) {
          searchDateTerm = q.replace(/tomm?orow/g, 'tomorrow');
        }
        const friendlyDateStr = GetFriendlyTaskDueDate(task.dueDate)?.toLowerCase() || '';
        const rawDateStr = task.dueDate ? String(task.dueDate).toLowerCase() : '';
        const inDueDate =
          friendlyDateStr.includes(searchDateTerm) ||
          rawDateStr.includes(searchDateTerm) ||
          (task.isOverdue && 'overdue'.includes(searchDateTerm));
        const inPriority = task.priority?.toLowerCase().includes(q);
        const inAssignedUser = task.assignedUser?.name?.toLowerCase().includes(q);
        const inAttachments = task.attachments?.some((att: any) => att.name?.toLowerCase().includes(q));
        const inNotes = task.notes?.some(
          (note: any) =>
            note.authorName?.toLowerCase().includes(q) ||
            note.noteText?.toLowerCase().includes(q)
        );
        const inCreatedBy = task.createdBy?.authorName?.toLowerCase().includes(q);
        const inUpdatedBy = task.updatedBy?.authorName?.toLowerCase().includes(q);

        const isSearchMatch =
          inTitle ||
          inDesc ||
          inStatus ||
          inStage ||
          inDueDate ||
          inPriority ||
          inAssignedUser ||
          inAttachments ||
          inNotes ||
          inCreatedBy ||
          inUpdatedBy;

        if (!isSearchMatch) return false;
      }

      // 2. Header Stage Filter Dropdown
      if (!isPlanReview && selectedStage !== TaskStage.ALL) {
        const currentStageID = getStageId(selectedStage);
        const isStageMatch = task.stage === selectedStage || (currentStageID !== null && task.stageID === currentStageID);
        if (!isStageMatch) return false;
      }

      // 3. Quick Dashboard Chip Filter (Due / Overdue / In-Progress / Completed / Upcoming)
      if (selectedDashboardChip) {
        const rawTask = {
          taskStatus: task.statusEnum !== undefined ? task.statusEnum : task.taskStatus,
          taskStatusName: task.status,
          dueOn: task.dueDate || task.dueOn
        };

        if (selectedDashboardChip === 'due' && !isTaskDue(rawTask)) return false;
        if (selectedDashboardChip === 'overdue' && !isTaskOverdue(rawTask)) return false;
        if (selectedDashboardChip === 'inProgress' && !isTaskInProgress(rawTask)) return false;
        if (selectedDashboardChip === 'completed' && !isTaskCompleted(rawTask)) return false;
        if (selectedDashboardChip === 'upcoming' && !isTaskUpcoming(rawTask)) return false;
      }

      // 4. Popover Status Filter (Active / Pending / In Progress / Completed / Cancelled)
      if (appliedStatus !== 'All') {
        const statusName = String(task.status || task.taskStatusName || '').toLowerCase().replace(/[^a-z]/g, '');
        if (appliedStatus === 'Active') {
          if (statusName !== 'pending' && statusName !== 'inprogress') return false;
        } else if (appliedStatus === 'Pending') {
          if (statusName !== 'pending') return false;
        } else if (appliedStatus === 'In Progress' || appliedStatus === 'In-Progress') {
          if (statusName !== 'inprogress') return false;
        } else if (appliedStatus === 'Completed') {
          if (statusName !== 'completed') return false;
        } else if (appliedStatus === 'Cancelled') {
          if (statusName !== 'cancelled') return false;
        }
      }

      return true;
    });
  }, [mappedTasks, searchQuery, selectedStage, isPlanReview, selectedDashboardChip, appliedStatus]);

  // Local UI Sorting (Most Relevant returns original API response order)
  const sortedTasks = useMemo(() => {
    if (selectedSort === 'Most Relevant') {
      return filteredTasks;
    }

    const list = [...filteredTasks];

    if (selectedSort === 'Due Date') {
      list.sort((a, b) => {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf();
      });
    } else if (selectedSort === 'Priority') {
      const priorityOrder: Record<string, number> = {
        Urgent: 1,
        High: 2,
        Normal: 3,
        Low: 4
      };
      list.sort((a, b) => {
        const pA = priorityOrder[a.priority] || 99;
        const pB = priorityOrder[b.priority] || 99;
        return pA - pB;
      });
    } else if (selectedSort === 'Created On') {
      list.sort((a, b) => {
        const dateA = a.createdBy?.createdDate || a.createdOn || '';
        const dateB = b.createdBy?.createdDate || b.createdOn || '';
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dayjs(dateB).valueOf() - dayjs(dateA).valueOf();
      });
    }

    return list;
  }, [filteredTasks, selectedSort]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 840, md: 840, lg: 850 },
          bgcolor: 'background.paper',
          boxShadow: theme.customShadows?.z1 || '0px 8px 24px rgba(0, 0, 0, 0.12)',
          borderLeft: `1px solid ${theme.palette.divider}`
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Top Header Bar */}
        <Box
          sx={{
            p: 1.5,
            px: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography variant="h4" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '17px' }}>
              Tasks
            </Typography>

            {/* Status Count Filter Chips */}
            <Stack direction="row" spacing={0.75} alignItems="center">
              {/* Due Chip */}
              <Box
                onClick={() => setSelectedDashboardChip(selectedDashboardChip === 'due' ? null : 'due')}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 1,
                  height: 35,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: `1px solid ${selectedDashboardChip === 'due' ? theme.palette.warning.main : alpha(theme.palette.warning.light, 0.3)}`,
                  bgcolor: selectedDashboardChip === 'due' ? alpha(theme.palette.warning.main, 0.12) : alpha(theme.palette.warning.light, 0.1),
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.warning.main, 0.15)
                  }
                }}
              >
                <Typography title='Click to Filter Due Tasks' variant="subtitle2" component="span" sx={{ fontWeight: 500, color: 'warning.main', fontSize: '15px', me: '1px' }}>
                  {statusCounts.due}
                </Typography>
                <Typography title='Click to Filter Due Tasks' variant="body2" component="span" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '14px' }}>
                  Due
                </Typography>
                {selectedDashboardChip === 'due' && (
                  <CloseOutlined style={{ fontSize: '12px', color: theme.palette.warning.main }} />
                )}
              </Box>

              {/* Overdue Chip */}
              <Box
                onClick={() => setSelectedDashboardChip(selectedDashboardChip === 'overdue' ? null : 'overdue')}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 1,
                  height: 35,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: `1px solid ${selectedDashboardChip === 'overdue' ? theme.palette.error.main : alpha(theme.palette.error.light, 0.3)}`,
                  bgcolor: selectedDashboardChip === 'overdue' ? alpha(theme.palette.error.main, 0.12) : alpha(theme.palette.error.light, 0.1),
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.error.main, 0.15)
                  }
                }}
              >
                <Typography title='Click to Filter Overdue Tasks' variant="subtitle2" component="span" sx={{ fontWeight: 500, color: 'error.main', fontSize: '15px', me: '1px' }}>
                  {statusCounts.overdue}
                </Typography>
                <Typography title='Click to Filter Overdue Tasks' variant="body2" component="span" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '14px' }}>
                  Overdue
                </Typography>
                {selectedDashboardChip === 'overdue' && (
                  <CloseOutlined style={{ fontSize: '12px', color: theme.palette.error.main }} />
                )}
              </Box>

              {/* In-Progress Chip */}
              <Box
                onClick={() => setSelectedDashboardChip(selectedDashboardChip === 'inProgress' ? null : 'inProgress')}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 1,
                  height: 35,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: `1px solid ${selectedDashboardChip === 'inProgress' ? theme.palette.primary.main : alpha(theme.palette.primary.light, 0.3)}`,
                  bgcolor: selectedDashboardChip === 'inProgress' ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.light, 0.1),
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.15)
                  }
                }}
              >
                <Typography title='Click to Filter In-Progress Tasks' variant="subtitle2" component="span" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '15px', me: '1px' }}>
                  {statusCounts.inProgress}
                </Typography>
                <Typography title='Click to Filter In-Progress Tasks' variant="body2" component="span" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '14px' }}>
                  In-Progress
                </Typography>
                {selectedDashboardChip === 'inProgress' && (
                  <CloseOutlined style={{ fontSize: '12px', color: theme.palette.primary.main }} />
                )}
              </Box>

              {/* Completed Chip */}
              <Box
                onClick={() => setSelectedDashboardChip(selectedDashboardChip === 'completed' ? null : 'completed')}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 1,
                  height: 35,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: `1px solid ${selectedDashboardChip === 'completed' ? theme.palette.success.main : alpha(theme.palette.success.light, 0.3)}`,
                  bgcolor: selectedDashboardChip === 'completed' ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.success.light, 0.1),
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.success.main, 0.15)
                  }
                }}
              >
                <Typography title='Click to Filter Completed Tasks' variant="subtitle2" component="span" sx={{ fontWeight: 500, color: 'success.main', fontSize: '15px', me: '1px' }}>
                  {statusCounts.completed}
                </Typography>
                <Typography title='Click to Filter Completed Tasks' variant="body2" component="span" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '14px' }}>
                  Completed
                </Typography>
                {selectedDashboardChip === 'completed' && (
                  <CloseOutlined style={{ fontSize: '12px', color: theme.palette.success.main }} />
                )}
              </Box>

              {/* Upcoming Chip */}
              <Box
                onClick={() => setSelectedDashboardChip(selectedDashboardChip === 'upcoming' ? null : 'upcoming')}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 1,
                  height: 35,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  border: `1px solid ${selectedDashboardChip === 'upcoming' ? (theme.palette.secondary?.main || '#722ED1') : alpha(theme.palette.secondary?.main || '#722ED1', 0.3)}`,
                  bgcolor: selectedDashboardChip === 'upcoming' ? alpha(theme.palette.secondary?.main || '#722ED1', 0.12) : alpha(theme.palette.secondary?.main || '#722ED1', 0.08),
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.secondary?.main || '#722ED1', 0.15)
                  }
                }}
              >
                <Typography title='Click to Filter Upcoming Tasks' variant="subtitle2" component="span" sx={{ fontWeight: 500, color: theme.palette.secondary?.main || '#722ED1', fontSize: '15px', me: '1px' }}>
                  {statusCounts.upcoming}
                </Typography>
                <Typography title='Click to Filter Upcoming Tasks' variant="body2" component="span" sx={{ fontWeight: 400, color: 'text.primary', fontSize: '14px' }}>
                  Upcoming
                </Typography>
                {selectedDashboardChip === 'upcoming' && (
                  <CloseOutlined style={{ fontSize: '12px', color: theme.palette.secondary?.main || '#722ED1' }} />
                )}
              </Box>
            </Stack>

            {/* Stage Dropdown Select Filter */}
            {!isPlanReview && (projectIdNumber !== null || taskModule === 1) && (
              <FormControl size="small" sx={{ width: 160, minWidth: 160, ml: 1 }}>
                <InputLabel id="stage-select-label" sx={{ fontSize: '13px' }}>
                  Stage
                </InputLabel>
                <Select
                  labelId="stage-select-label"
                  id="stage-select"
                  value={selectedStage}
                  label="Stage"
                  onChange={(e) => setSelectedStage(e.target.value as string)}
                  sx={{
                    width: '100%',
                    borderRadius: 1.5,
                    height: 36,
                    fontSize: '13px',
                    fontWeight: 400,
                    bgcolor: 'background.paper',
                    '& .MuiSelect-select': {
                      py: 0.75,
                      px: 1.25,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                >
                  {Object.values(TaskStage).map((stage) => (
                    <MenuItem key={stage} value={stage} sx={{ fontSize: '14px' }}>
                      {stage}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              title='Refresh Data'
              size="small"
              onClick={handleRefresh}
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <ReloadIcon />
            </IconButton>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.lighter' }
              }}
            >
              <CloseOutlined style={{ fontSize: '16px' }} />
            </IconButton>
          </Stack>
        </Box>

        {/* Toolbar & Filter Bar */}
        <Box
          sx={{
            p: 2,
            px: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Search input */}
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              size="medium"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined style={{ color: theme.palette.text.secondary, fontSize: '16px' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0.25 }}>
                      <CloseOutlined style={{ fontSize: '14px', color: theme.palette.text.secondary }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: 'grey.50'
                }
              }}
            />

            {/* Board View Button */}
            {user?.userType !== 1 && (
              <Tooltip title="Task Board" arrow placement="top">
                <IconButton
                  onClick={() => {
                    if (onClose) onClose();
                    let stageId: number | null = null;
                    if (selectedStage && selectedStage !== TaskStage.ALL) {
                      stageId = getStageId(selectedStage);
                    } else if (taskModule === 0 || !projectIdNumber) {
                      stageId = 0;
                    }
                    navigate('/ppf/taskboard', {
                      state: {
                        fromDrawer: true,
                        projectId: projectIdNumber,
                        stageId: stageId
                      }
                    });
                  }}
                  sx={{
                    flexShrink: 0,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    width: 38,
                    height: 38,
                    bgcolor: 'grey.100',
                    color: 'text.primary',
                    '&:hover': {
                      bgcolor: 'grey.200'
                    }
                  }}
                >
                  <ModernBoardIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* List View Button */}
            {user?.userType !== 1 && (
              <Tooltip title="Task List" arrow placement="top">
                <IconButton
                  onClick={() => {
                    if (onClose) onClose();
                    let stageId: number | null = null;
                    if (selectedStage && selectedStage !== TaskStage.ALL) {
                      stageId = getStageId(selectedStage);
                    } else if (taskModule === 0 || !projectIdNumber) {
                      stageId = 0;
                    }
                    navigate('/ppf/taskList', {
                      state: {
                        fromDrawer: true,
                        projectId: projectIdNumber,
                        stageId: stageId
                      }
                    });
                  }}
                  sx={{
                    flexShrink: 0,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    width: 38,
                    height: 38,
                    bgcolor: 'grey.100',
                    color: 'text.primary',
                    '&:hover': {
                      bgcolor: 'grey.200'
                    }
                  }}
                >
                  <ModernListIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Filter icon button */}
            <Tooltip title="Filter" arrow placement='top'>
              <IconButton
                onClick={handleOpenFilterPopover}
                sx={{
                  flexShrink: 0,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  width: 38,
                  height: 38,
                  bgcolor: isFilterOpen || activeFilterCount > 0 ? 'primary.lighter' : 'grey.100',
                  color: isFilterOpen || activeFilterCount > 0 ? 'primary.main' : 'text.primary',
                  '&:hover': {
                    bgcolor: 'grey.200'
                  }
                }}
              >
                <Badge
                  badgeContent={activeFilterCount}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '10px',
                      height: 16,
                      minWidth: 16,
                      padding: '0 4px',
                      borderRadius: '10px',
                      top: -4,
                      right: -4
                    }
                  }}
                >
                  <FilterIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Sort dropdown control */}
            <Tooltip title={selectedSort !== 'Most Relevant' ? `Sort by: ${selectedSort}` : "Sort by Most Relevant"} arrow placement='top'>
              <Box
                onClick={handleOpenSortMenu}
                sx={{
                  flexShrink: 0,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: '6px',
                  height: 38,
                  px: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'grey.50'
                  }
                }}
              >
                <Badge
                  badgeContent={selectedSort !== 'Most Relevant' ? 1 : 0}
                  color="primary"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '10px',
                      height: 16,
                      minWidth: 16,
                      padding: '0 4px',
                      borderRadius: '10px',
                      top: -4,
                      right: -4
                    }
                  }}
                >
                  <SortFilterIcon />
                </Badge>
                <DownOutlined style={{ fontSize: '11px', color: theme.palette.text.secondary }} />
              </Box>
            </Tooltip>

            {/* Add Task Primary Button */}
            {allowAdd && (
              <Tooltip title="Add Task" arrow placement='top'>
                <IconButton
                  onClick={() => handleOpenAddTaskModal(null)}
                  sx={{
                    flexShrink: 0,
                    bgcolor: 'primary.main',
                    color: '#fff',
                    borderRadius: 1.5,
                    width: 38,
                    height: 38,
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  <PlusOutlined style={{ fontSize: '15px', color: '#fff' }} />
                </IconButton>
              </Tooltip>
            )}
            {/* More Options Button */}
            {(allowAddTaskTemplate || taskTemplates?.length > 0) && (
              <Tooltip title="More Option" arrow placement="top">
                <IconButton
                  onClick={handleOpenMoreMenu}
                  sx={{
                    flexShrink: 0,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    width: 38,
                    height: 38,
                    color: 'text.secondary'
                  }}
                >
                  <ModernIconMore />
                </IconButton>
              </Tooltip>
            )}

            {/* Sort Filter Context Menu */}

            <Menu
              anchorEl={sortAnchorEl}
              open={isSortMenuOpen}
              onClose={handleCloseSortMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              PaperProps={{
                elevation: 3,
                sx: {
                  borderRadius: '12px',
                  minWidth: 160,
                  p: 0.5,
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)'
                }
              }}
            >
              {['Most Relevant', 'Due Date', 'Priority', 'Created On'].map((option) => {
                const isSelected = selectedSort === option;
                return (
                  <MenuItem
                    key={option}
                    onClick={() => handleSelectSort(option)}
                    sx={{
                      borderRadius: '8px',
                      fontWeight: isSelected ? 500 : 400,
                      fontSize: '14px',
                      color: 'text.primary',
                      py: 1,
                      px: 1.5,
                      bgcolor: isSelected ? 'grey.100' : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected ? 'grey.200' : 'action.hover'
                      }
                    }}
                  >
                    {option}
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Top Right More Options Menu */}
            <Menu
              anchorEl={moreMenuAnchor}
              open={isMoreMenuOpen}
              onClose={handleCloseMoreMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              PaperProps={{
                elevation: 4,
                sx: {
                  minWidth: 210,
                  maxHeight: 500,
                  borderRadius: 2.5,
                  p: 1,
                  mt: 0.5,
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)'
                }
              }}
            >
              {/* 1. Save As Template (Filled Blue Button) */}
              {allowAddTaskTemplate && (
                <MenuItem
                  onClick={() => {
                    setOpenTaskTemplateModal(true)
                    handleCloseMoreMenu()
                  }}
                  sx={{
                    bgcolor: 'primary.main',
                    color: '#fff',
                    borderRadius: 1.5,
                    py: 1,
                    px: 1.5,
                    mb: 0.5,
                    fontWeight: 500,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  <TemplateIcon style={{ fontSize: '18px', color: '#fff' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#fff', fontSize: '14px' }}>
                    Save As Template
                  </Typography>
                </MenuItem>
              )}
              {allowAddTaskTemplate && taskTemplates?.length > 0 && (
                <Divider sx={{ my: 1, borderColor: 'divider' }} />
              )}

              {/* 2. Subtitle: Import Template */}
              {taskTemplates?.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    display: 'block',
                    color: 'text.secondary',
                    fontSize: '13px',
                    fontWeight: 400
                  }}
                >
                  Import Template
                </Typography>
              )}

              {/* 3. Template Items (Mapped from array) */}
              {taskTemplates?.map((template) => (
                <MenuItem
                  key={template.taskTemplateID}
                  onClick={() => setOpenTaskTemplateSelectID(template.taskTemplateID)}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    fontSize: '13.5px',
                    color: 'text.primary',
                    gap: 1.25
                  }}
                >
                  <TemplateIcon style={{ fontSize: '18px', color: theme.palette.text.primary }} />
                  {template.templateName}
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Box>

        {/* Scrollable Tasks Body */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: 2.5,
            bgcolor: 'grey.50'
          }}
        >
          {!allowView ? (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Box sx={{ height: { xs: 200, sm: 260 }, width: 'auto', mb: 2 }}>
                <CardMedia component="img" src={nopermission} alt="no-permission" sx={{ height: 1, objectFit: 'contain' }} />
              </Box>
              <Typography align="center" variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                Access Denied
              </Typography>
              <Typography align="center" color="text.secondary" variant="body2">
                Permission denied—reach out to your admin for help.
              </Typography>
            </Box>
          ) : loading ? (
            <Stack spacing={0}>
              {[1, 2, 3].map((item) => (
                <Card
                  key={item}
                  elevation={0}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderLeft: `4px solid ${theme.palette.grey[300]}`,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Stack spacing={1.5}>
                    {/* Header Row: Title + Stage + Status */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Skeleton variant="text" width="45%" height={24} />
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Skeleton variant="rounded" width={80} height={26} sx={{ borderRadius: '4px' }} />
                        <Skeleton variant="rounded" width={90} height={26} sx={{ borderRadius: '4px' }} />
                      </Stack>
                    </Stack>

                    {/* Details Row: Due Date + Priority + Assigned User */}
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Skeleton variant="text" width={110} height={18} />
                      <Box sx={{ width: 2, height: 16, bgcolor: 'grey.200', borderRadius: 1 }} />
                      <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: 1 }} />
                      <Box sx={{ width: 2, height: 16, bgcolor: 'grey.200', borderRadius: 1 }} />
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Skeleton variant="circular" width={18} height={18} />
                        <Skeleton variant="text" width={85} height={18} />
                      </Stack>
                    </Stack>

                    {/* Description */}
                    <Skeleton variant="text" width="90%" height={20} />

                    {/* Attachments */}
                    <Stack direction="row" spacing={1}>
                      <Skeleton variant="rounded" width={115} height={26} sx={{ borderRadius: 1.5 }} />
                      <Skeleton variant="rounded" width={95} height={26} sx={{ borderRadius: 1.5 }} />
                    </Stack>

                    <Divider sx={{ my: 0.5, borderColor: theme.palette.divider }} />

                    {/* Notes Box */}
                    <Box
                      sx={{
                        bgcolor: 'grey.50',
                        px: 1.25,
                        py: 1,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Skeleton variant="text" width="70%" height={18} />
                      <Skeleton variant="circular" width={16} height={16} />
                    </Box>

                    {/* Footer Meta */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Skeleton variant="text" width={65} height={20} />
                      <Skeleton variant="text" width={140} height={16} />
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : sortedTasks.length === 0 ? (
            <Box sx={{ textCenter: 'center', py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No tasks found.
              </Typography>
            </Box>
          ) : (
            sortedTasks.map((task: any) => (
              <TaskCard
                key={task.taskID}
                task={task}
                allowEdit={allowEdit}
                allowDelete={allowDelete}
                allowView={allowView}
                allowAdd={allowAdd}
                onEdit={(t: any) => handleOpenAddTaskModal(t)}
                onDelete={handleDeleteTask}
              />
            ))
          )}
        </Box>
        {/* Filters Popover (Matching Screenshot Design) */}
        <Popover
          open={isFilterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilterPopover}

          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{
            elevation: 4,
            sx: {
              width: 340,
              p: 1.5,
              borderRadius: '12px',
              border: `1px solid ${theme.palette.grey[300]}`,
              boxShadow: theme.customShadows?.z1 || '0px 8px 24px rgba(0, 0, 0, 0.12)',
              mt: 1
            }
          }}
        >
          {/* Header: Filters + Clear + Apply Filters */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '16px' }}>
                Filters
              </Typography>
              {(filterStatus !== 'All' ||
                filterDueOn !== 'All' ||
                filterAssignedTo !== 'All' ||
                filterPriority !== 'All' ||
                filterCreatedOn !== 'All' ||
                appliedStatus !== 'All' ||
                appliedDueOn !== 'All' ||
                appliedAssignedTo !== 'All' ||
                appliedPriority !== 'All' ||
                appliedCreatedOn !== 'All') && (
                  <Button
                    onClick={handleClearFilters}
                    size="small"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: '13.5px',
                      textTransform: 'none',
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                    }}
                  >
                    Clear
                  </Button>
                )}
            </Stack>
            <Button
              onClick={handleApplyFilters}
              endIcon={<FunnelPlotOutlined />}
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                fontSize: '13.5px',
                textTransform: 'none',
                p: 0,
                minWidth: 'auto',
                '&:hover': { bgcolor: 'transparent' }
              }}
            >
              Apply Filters
            </Button>
          </Stack>

          {/* Filter Options List */}
          <Stack spacing={1.75}>
            {/* Status Filter */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13.5px', fontWeight: 400, minWidth: 90 }}>
                Status
              </Typography>
              <FormControl size="small" sx={{ flexGrow: 1 }}>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  sx={{
                    height: 36,
                    borderRadius: 2,
                    fontSize: '13px',
                    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 }
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: '13px' }}>All</MenuItem>
                  <MenuItem value="Active" sx={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ActiveTaskIcon /> Active
                  </MenuItem>
                  <MenuItem value="Pending" sx={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PendingChipIcon /> Pending
                  </MenuItem>
                  <MenuItem value="In Progress" sx={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InprogressChipIcon /> In Progress
                  </MenuItem>
                  <MenuItem value="Completed" sx={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CompleteChipIcon /> Completed
                  </MenuItem>
                  <MenuItem value="Cancelled" sx={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <CloseOutlined style={{ fontSize: 14, color: 'red' }} /> Cancelled
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Due On Filter */}
            <Stack direction="column" spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 400, minWidth: 90 }}>
                  Due On
                </Typography>
                <FormControl size="small" sx={{ flexGrow: 1 }}>
                  <Select
                    value={filterDueOn}
                    onChange={(e) => setFilterDueOn(e.target.value)}
                    sx={{ height: 36, borderRadius: 2, fontSize: '13px' }}
                  >
                    <MenuItem value="All" sx={{ fontSize: '13px' }}>All</MenuItem>
                    <MenuItem value="Today" sx={{ fontSize: '13px' }}>Today</MenuItem>
                    <MenuItem value="Tomorrow" sx={{ fontSize: '13px' }}>Tomorrow</MenuItem>
                    <MenuItem value="This Week" sx={{ fontSize: '13px' }}>This Week</MenuItem>
                    <MenuItem value="Next Week" sx={{ fontSize: '13px' }}>Next Week</MenuItem>
                    <MenuItem value="Later" sx={{ fontSize: '13px' }}>Later</MenuItem>
                    <MenuItem value="Custom" sx={{ fontSize: '13px' }}>Custom</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              {filterDueOn === 'Custom' && (
                <Box sx={{ pl: '106px' }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateRangePicker
                      value={sanitizeDateRange(customDueRange)}
                      onChange={(newValue) => setCustomDueRange(sanitizeDateRange(newValue))}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              )}
            </Stack>

            {/* Assigned To Filter */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 400, minWidth: 90 }}>
                Assigned To
              </Typography>
              <FormControl size="small" sx={{ flexGrow: 1 }}>
                <Select
                  value={filterAssignedTo}
                  onChange={(e) => setFilterAssignedTo(e.target.value)}
                  sx={{ height: 36, borderRadius: 2, fontSize: '13px' }}
                >
                  <MenuItem value="All" sx={{ fontSize: '13px' }}>All</MenuItem>
                  {userLookupData?.map((user: any) => (
                    <MenuItem key={user.userID} value={user.userID} sx={{ fontSize: '13px' }}>
                      {user.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Priority Filter */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 400, minWidth: 90 }}>
                Priority
              </Typography>
              <FormControl size="small" sx={{ flexGrow: 1 }}>
                <Select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  sx={{ height: 36, borderRadius: 2, fontSize: '13px' }}
                >
                  <MenuItem value="All" sx={{ fontSize: '13px' }}>All</MenuItem>
                  <MenuItem value="Urgent" sx={{ fontSize: '13px' }}>Urgent</MenuItem>
                  <MenuItem value="High" sx={{ fontSize: '13px' }}>High</MenuItem>
                  <MenuItem value="Normal" sx={{ fontSize: '13px' }}>Normal</MenuItem>
                  <MenuItem value="Low" sx={{ fontSize: '13px' }}>Low</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Created On Filter */}
            <Stack direction="column" spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 400, minWidth: 90 }}>
                  Created On
                </Typography>
                <FormControl size="small" sx={{ flexGrow: 1 }}>
                  <Select
                    value={filterCreatedOn}
                    onChange={(e) => setFilterCreatedOn(e.target.value)}
                    sx={{ height: 36, borderRadius: 2, fontSize: '13px' }}
                  >
                    <MenuItem value="All" sx={{ fontSize: '13px' }}>All</MenuItem>
                    <MenuItem value="Today" sx={{ fontSize: '13px' }}>Today</MenuItem>
                    <MenuItem value="Yesterday" sx={{ fontSize: '13px' }}>Yesterday</MenuItem>
                    <MenuItem value="This Week" sx={{ fontSize: '13px' }}>This Week</MenuItem>
                    <MenuItem value="Last Week" sx={{ fontSize: '13px' }}>Last Week</MenuItem>
                    <MenuItem value="Before" sx={{ fontSize: '13px' }}>Before</MenuItem>
                    <MenuItem value="Custom" sx={{ fontSize: '13px' }}>Custom</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              {filterCreatedOn === 'Custom' && (
                <Box sx={{ pl: '106px' }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateRangePicker
                      value={sanitizeDateRange(customCreatedRange)}
                      onChange={(newValue) => setCustomCreatedRange(sanitizeDateRange(newValue))}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              )}
            </Stack>
          </Stack>
        </Popover>
      </Box>

      {/* Add / Edit Task Modal Form */}
      <TaskForm
        open={openAddTaskModal}
        closeModal={() => {
          setOpenAddTaskModal(false)
          setSelectedTaskForEdit(null)
        }
        }
        task={selectedTaskForEdit}
        onSave={() => {
          refetchTasks();
        }}
        stageName={selectedStage !== TaskStage.ALL ? selectedStage : undefined}
      />

      {!!openTaskTemplateSelectID && (
        <SelectTaskTemplateModal
          open={!!openTaskTemplateSelectID}
          onClose={() => {
            setOpenTaskTemplateSelectID(null)
            handleCloseMoreMenu()
          }}
          handleConfirm={() => handleConfirmTaskTemplate()}
          isApplying={isApplying}
        />
      )}

      {openTaskTemplateModal && (
        <TaskTemplateModal
          open
          handleClose={() => {
            setOpenTaskTemplateModal(false);
          }}
          isSaveAsModal
          taskModule={taskModule}
          taskIDs={apiTasks?.map((task: TaskItem) => task.taskID) || []}
        />
      )}
    </Drawer>
  );
}
